import React from 'react';
import { Link } from 'react-router-dom';

const AppsLandingPage = () => {
  const apps = [
    {
      id: 1,
      name: "EventIQ",
      description: "Sustainable digital event management platform with QR-based invitations, RSVP tracking, and sustainability analytics.",
      status: "Live",
      category: "Productivity",
      icon: "🎯",
      color: "green",
      metrics: {
        downloads: "10K+",
        rating: "4.8",
        users: "500+"
      },
      features: ["QR Invitations", "RSVP Tracking", "Media Sharing", "Sustainability Analytics"],
      url: "/apps/eventiq"
    }
    // Future apps can be added here
    // {
    //   id: 2,
    //   name: "AppName2",
    //   description: "Description of future app...",
    //   status: "Coming Soon",
    //   category: "Category",
    //   icon: "🚀",
    //   color: "blue",
    //   metrics: {
    //     downloads: "Coming Soon",
    //     rating: "Coming Soon",
    //     users: "Coming Soon"
    //   },
    //   features: ["Feature 1", "Feature 2", "Feature 3"],
    //   url: "/apps/future-app"
    // }
  ];

  const getStatusBadge = (status: any) => {
    const statusStyles: any = {
      "Live": "bg-green-100 text-green-800 border-green-200",
      "Coming Soon": "bg-yellow-100 text-yellow-800 border-yellow-200",
      "Beta": "bg-blue-100 text-blue-800 border-blue-200"
    };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${statusStyles[status]}`}>
        {status === "Live" && <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>}
        {status}
      </span>
    );
  };

  const getColorClass = (color: string) => {
    const colorClasses: any = {
      "green": "from-green-500 to-green-600",
      "blue": "from-blue-500 to-blue-600",
      "purple": "from-purple-500 to-purple-600",
      "orange": "from-orange-500 to-orange-600"
    };
    return colorClasses[color] || colorClasses.green;
  };

  return (
    <div className="min-h-screen bg-gray-50">


      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-50 to-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Our <span className="text-green-600">Applications</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Discover innovative mobile applications designed to simplify your life and enhance productivity.
              Each app is crafted with care and attention to detail.
            </p>
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white text-gray-700 text-sm font-medium shadow-sm">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
              {apps.length} {apps.length === 1 ? 'Application' : 'Applications'} and growing
            </div>
          </div>
        </div>
      </section>

      {/* Apps Grid Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {apps.map((app) => (
              <div key={app.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                {/* App Header with Gradient */}
                <div className={`bg-gradient-to-r ${getColorClass(app.color)} p-6`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                        <span className="text-2xl">{app.icon}</span>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white">{app.name}</h3>
                        <div className="flex items-center space-x-2 mt-1">
                          {getStatusBadge(app.status)}
                          <span className="text-white/80 text-sm">{app.category}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* App Content */}
                <div className="p-6">
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {app.description}
                  </p>

                  {/* Key Metrics */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900">{app.metrics.downloads}</div>
                      <div className="text-xs text-gray-500">Downloads</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900">{app.metrics.rating}/5</div>
                      <div className="text-xs text-gray-500">Rating</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900">{app.metrics.users}</div>
                      <div className="text-xs text-gray-500">Active Users</div>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Key Features</h4>
                    <div className="flex flex-wrap gap-2">
                      {app.features.map((feature, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* CTA Buttons */}
                  <div className="space-y-3">
                    <Link
                      to={app.url}
                      className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-green-600 hover:bg-green-700 transition-all duration-300 shadow-md hover:shadow-lg"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      View App Details
                    </Link>

                    {app.status === "Live" && (
                      <div className="grid grid-cols-2 gap-3">
                        <a
                          href="https://play.google.com/store/apps/details?id=in.binalogy.eventplanner"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
                        >
                          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                            <path fill="currentColor" d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                          </svg>
                          Google Play
                        </a>
                        <button
                          disabled
                          className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-400 bg-gray-100 cursor-not-allowed relative"
                        >
                          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                            <path fill="currentColor" d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" />
                          </svg>
                          App Store
                          <span className="absolute -top-2 -right-2 bg-yellow-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                            Soon
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Coming Soon Placeholder */}
          <div className="mt-16 text-center">
            <div className="bg-white rounded-2xl shadow-sm p-12 border-2 border-dashed border-gray-300">
              <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl">🚀</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">More Apps Coming Soon</h3>
              <p className="text-gray-600 max-w-md mx-auto mb-6">
                We're constantly working on new innovative applications to solve everyday problems and enhance productivity.
              </p>
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-gray-100 text-gray-600 text-sm font-medium">
                Stay tuned for updates
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Developer Info Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Behind the Apps</h2>
            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-8 max-w-4xl mx-auto">
              <div className="flex flex-col md:flex-row items-center justify-between">
                <div className="text-center md:text-left mb-6 md:mb-0">
                  <h3 className="text-2xl font-bold text-gray-900">Divya HM</h3>
                  <p className="text-gray-600 mt-2">Developer</p>
                  <div className="flex flex-wrap gap-4 mt-4 text-sm text-gray-500">
                    <div className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      Bangalore, India
                    </div>
                    <div className="flex items-center">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                      divya@binalogy.in
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">Binalogy</div>
                    <div className="text-gray-600 text-sm mt-1">Innovating for a better tomorrow</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-2xl font-bold text-green-400 mb-4">Binalogy Apps</h3>
              <p className="text-gray-400">
                Creating innovative mobile applications that make life easier and more productive.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Applications</h4>
              <ul className="space-y-2">
                {apps.map(app => (
                  <li key={app.id}>
                    <Link to={app.url} className="text-gray-400 hover:text-white transition">
                      {app.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><a href="/privacy-policy" className="text-gray-400 hover:text-white transition">Privacy Policy</a></li>
                <li><a href="/terms-of-use" className="text-gray-400 hover:text-white transition">Terms of Use</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Developer: Divya HM</li>
                <li>Company: Binalogy</li>
                <li>Email: divya@binalogy.in</li>
                <li>Location: Bangalore, India</li>
              </ul>
            </div>
          </div>


        </div>
      </footer>
    </div>
  );
};

export default AppsLandingPage;